import { Text } from '@/components/text';
import { IconSymbol } from '@/components/ui/icon-symbol';
import { Colors } from '@/constants/theme';
import { ModelType } from '@/types/model';
import { ScenarioType } from '@/types/scenario';
import { ScenarioPresentation } from '@/utils/scenario-presentation';
import { MaterialIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { PartnerMarginInput } from './partner-margin-input';
import { ScenarioBadge } from './scenario-badge';
import { Card } from './ui/card';
import { Tooltip } from './ui/tooltip';
import { View } from './ui/tw-wrappers';

export function ScenarioDetail({
  presentation,
  modelType,
  type,
}: {
  presentation: ScenarioPresentation;
  modelType: ModelType;
  type?: ScenarioType;
}) {
  const { t } = useTranslation();
  return (
    <View className='gap-4'>
      <Card
        backgroundColor='white'
        className='group-hover:border-primary border-2 border-transparent transition-all duration-300'>
        <View
          style={[
            {
              gap: 4,
              marginBottom: 10,
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: 30,
            },
          ]}>
          <Text style={{ fontSize: 18, fontWeight: '700' }}>{presentation.title}</Text>
          {type === 'localPartner' && modelType === 'compare' && <PartnerMarginInput />}
        </View>
        <View style={styles.highlightContainer}>
          <Text style={styles.highlightLabel}>{presentation.highlight.label}</Text>
          <Text style={styles.highlightValue}>{presentation.highlight.value}</Text>
          {modelType === 'compare' && type && (
            <ScenarioBadge type={type} style={{ alignSelf: 'center', marginTop: 16 }} />
          )}
        </View>
      </Card>

      {/* invoicing */}
      <Card
        backgroundColor='white'
        className='group-hover:border-primary border-2 border-transparent transition-all duration-300'>
        <View>
          <View style={styles.sectionHeader}>
            <IconSymbol name={presentation.sections.invoicing.icon as any} size={16} color='#305785' />
            <Text type='defaultBold' style={styles.sectionTitle}>
              {presentation.sections.invoicing.title}
            </Text>
          </View>
          <View style={styles.divider} />
          <View style={styles.content}>
            {modelType === 'benchmarking' && type !== 'internationalInvoicing' && (
              <Row
                prefixValue='USD/'
                label={presentation.sections.invoicing.exchangeRate.label}
                value={presentation.sections.invoicing.exchangeRate.value}
              />
            )}
            <Row
              label={presentation.sections.invoicing.localInvoicingAmount.label}
              value={presentation.sections.invoicing.localInvoicingAmount.value}
            />
            <Row
              label={presentation.sections.invoicing.vatRate.label}
              value={presentation.sections.invoicing.vatRate.value}
              tooltipContent={t('scenarios.card.localVatTooltip')}
            />
            <Row
              label={presentation.sections.invoicing.estimatedLocalInvoice.label}
              value={presentation.sections.invoicing.estimatedLocalInvoice.value}
            />
          </View>
        </View>
      </Card>

      {/* costs */}
      <Card
        backgroundColor='white'
        className='group-hover:border-primary border-2 border-transparent transition-all duration-300'>
        <View>
          <View style={styles.sectionHeader}>
            <IconSymbol name={presentation.sections.costs.icon as any} size={16} color='#305785' />
            <Text type='defaultBold' style={styles.sectionTitle}>
              {presentation.sections.costs.title}
            </Text>
          </View>
          <View style={styles.divider} />
          <View style={[styles.content, { minHeight: 90 }]}>
            <Row
              label={presentation.sections.costs.netRevenueExclusions.label}
              value={presentation.sections.costs.netRevenueExclusions.value}
              tooltipContent={t('scenarios.card.netRevenueExclusionsTooltip')}
            />
            {type === 'localPartner' && presentation.sections.costs.sharedMargin && (
              <Row
                label={presentation.sections.costs.sharedMargin.label}
                value={presentation.sections.costs.sharedMargin.value}
              />
            )}
            {type === 'localSubsidiary' && presentation.sections.costs.financialStructure && (
              <Row
                label={presentation.sections.costs.financialStructure.label}
                value={presentation.sections.costs.financialStructure.value}
              />
            )}
            {type === 'internationalInvoicing' && presentation.sections.costs.indirectCosts && (
              <Row
                label={presentation.sections.costs.indirectCosts.label}
                value={presentation.sections.costs.indirectCosts.value}
              />
            )}
          </View>
        </View>
      </Card>

      {/* results */}
      <Card
        backgroundColor='white'
        className='group-hover:border-primary border-2 border-transparent transition-all duration-300'>
        <View>
          <View style={styles.sectionHeader}>
            <IconSymbol name={presentation.sections.results.icon as any} size={16} color='#305785' />
            <Text type='defaultBold' style={styles.sectionTitle}>
              {presentation.sections.results.title}
            </Text>
          </View>
          <View style={styles.divider} />
          <View style={[styles.content, { minHeight: 90 }]}>
            <Row
              label={presentation.sections.results.estimatedPayment.label}
              value={presentation.sections.results.estimatedPayment.value}
            />
            {type !== 'internationalInvoicing' && (
              <Row
                label={presentation.sections.results.estimatedPaymentUSD.label}
                value={presentation.sections.results.estimatedPaymentUSD.value}
              />
            )}
          </View>
        </View>
      </Card>
    </View>
  );
}

function Row({
  label,
  value,
  tooltipContent,
  prefixLabel,
  prefixValue,
}: {
  label: string;
  value: string;
  tooltipContent?: string;
  prefixLabel?: string;
  prefixValue?: string;
}) {
  return (
    <View style={styles.row}>
      <View style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}>
        <Text style={{ color: Colors.light.placeholderText, fontSize: 14, paddingRight: 8, lineHeight: 20 }}>
          {prefixLabel ? `${prefixLabel} ${label}` : label}
        </Text>
        {tooltipContent && (
          <Tooltip content={tooltipContent}>
            <MaterialIcons name='info' size={18} color={Colors.light.primary} />
          </Tooltip>
        )}
      </View>
      <Text style={{ fontWeight: '600', fontSize: 14, color: '#11181C', flex: 1, lineHeight: 20 }}>
        {prefixValue ? `${prefixValue}${value}` : value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 11,
    color: '#305785',
    textTransform: 'uppercase',
  },
  content: {
    gap: 10,
    marginVertical: 4,
  },
  row: {
    gap: 0,
  },
  divider: {
    height: 1,
    backgroundColor: '#305785',
    marginBottom: 8,
  },
  highlightContainer: {
    backgroundColor: '#EFF6FF',
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    // marginBottom: 16,
  },
  highlightLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#305785',
    textTransform: 'uppercase',
    textAlign: 'center',
    marginHorizontal: 16,
  },
  highlightValue: {
    fontSize: 28, // Big font for total
    fontWeight: '800',
    color: '#305785', // primary color
  },
});
